/**
 * The outbox: actions saved on the phone while offline (sign-ons, checklists, dockets sent for sign-off, incident
 * reports and their photos, shift replies), sent in the order they were saved once the phone is back online.
 *
 * Each action has its own id, sent as the Idempotency-Key, so the server does it once however often it arrives (a
 * send that timed out after the server got it is safe to repeat). A later action can use an earlier one's answer:
 * a path like "/incidents/{a1b2.incident.id}/photos" waits for action a1b2 and takes `incident.id` from its answer.
 *
 * Sending stops at the first action that can't go yet (no signal, the server busy, or an earlier one it depends on);
 * an action the server refuses (4xx) is kept, marked failed with the server's message, for the person to see and
 * remove.
 */
import { store, type KV } from "./store";

export type Part = string | { blob: Blob; name: string };
export type Body = { kind: "none" } | { kind: "json"; json: string } | { kind: "form"; parts: [string, Part][] };
export interface QueuedAction {
  id: string;
  /** Relative to /crew, e.g. "/shifts/<id>/checklists/start/submit". */
  path: string;
  method: string;
  body: Body;
  /** What it is, in plain words: "Signed on to the SWMS". */
  label: string;
  bookingId: string | null;
  queuedAt: string;
  seq: number;
  state: "waiting" | "failed";
  error: string | null;
}
export interface SendResult { status: number; json: unknown; message?: string }
export type Sender = (a: QueuedAction, path: string, body: BodyInit | undefined) => Promise<SendResult>;

export const OUTBOX_EVENT = "ops-outbox";
const notify = () => {
  if (typeof window !== "undefined") window.dispatchEvent(new Event(OUTBOX_EVENT));
};
const db = (): KV => store();

export const newActionId = (): string =>
  typeof crypto !== "undefined" && "randomUUID" in crypto ? crypto.randomUUID() : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;

export async function bodyFrom(init: RequestInit): Promise<Body> {
  if (init.body === undefined || init.body === null) return { kind: "none" };
  if (typeof init.body === "string") return { kind: "json", json: init.body };
  if (init.body instanceof FormData) {
    const parts: [string, Part][] = [];
    init.body.forEach((v, k) => parts.push([k, typeof v === "string" ? v : { blob: v, name: (v as File).name || "file" }]));
    return { kind: "form", parts };
  }
  throw new Error("This can't be saved to send later.");
}

export function bodyInit(b: Body): BodyInit | undefined {
  if (b.kind === "none") return undefined;
  if (b.kind === "json") return b.json;
  const f = new FormData();
  for (const [k, v] of b.parts) {
    if (typeof v === "string") f.append(k, v);
    else f.append(k, v.blob, v.name);
  }
  return f;
}

/** `replace`: a newer copy of the same thing (answers saved so far) takes the place of a waiting one, keeping its turn. */
export async function enqueue(a: Omit<QueuedAction, "queuedAt" | "seq" | "state" | "error"> & { queuedAt?: string }, replace = false): Promise<QueuedAction> {
  const all = await db().all<QueuedAction>("outbox");
  const older = replace ? all.map((x) => x.value).find((x) => x.state === "waiting" && x.method === a.method && x.path === a.path) : undefined;
  if (older) await db().del("outbox", older.id);
  const seq = older ? older.seq : Math.max(0, ...all.map((x) => x.value.seq)) + 1;
  const row: QueuedAction = { ...a, queuedAt: a.queuedAt ?? new Date().toISOString(), seq, state: "waiting", error: null };
  await db().put("outbox", row.id, row);
  notify();
  return row;
}

export async function pending(): Promise<QueuedAction[]> {
  return (await db().all<QueuedAction>("outbox")).map((x) => x.value).sort((a, b) => a.seq - b.seq);
}

export async function remove(id: string): Promise<void> {
  await db().del("outbox", id);
  notify();
}

const pick = (v: unknown, path: string[]): unknown => path.reduce<unknown>((o, k) => (o && typeof o === "object" ? (o as Record<string, unknown>)[k] : undefined), v);

/** Fill "{actionId.field.path}" from earlier answers; null while one is still to come. */
async function resolvePath(path: string): Promise<string | null> {
  const refs = [...path.matchAll(/\{([^.}]+)\.([^}]+)\}/g)];
  let out = path;
  for (const m of refs) {
    const result = await db().get<unknown>("results", m[1]!);
    const value = result === undefined ? undefined : pick(result, m[2]!.split("."));
    if (value === undefined || value === null) return null;
    out = out.replace(m[0], encodeURIComponent(String(value)));
  }
  return out;
}

let running: Promise<FlushResult> | null = null;
export interface FlushResult { sent: number; failed: number; left: number }

/** Send what can be sent, in order. Safe to call often: one run at a time. */
export function flush(send: Sender): Promise<FlushResult> {
  return (running ??= (async () => {
    let sent = 0;
    let failed = 0;
    try {
      for (const a of await pending()) {
        if (a.state === "failed") continue;
        const path = await resolvePath(a.path);
        if (path === null) {
          // It needs an earlier action's answer. If that action failed, this one can't go either.
          const deps = [...a.path.matchAll(/\{([^.}]+)\./g)].map((m) => m[1]!);
          const all = await pending();
          if (deps.some((id) => all.find((x) => x.id === id)?.state === "failed" || !all.some((x) => x.id === id))) {
            await db().put("outbox", a.id, { ...a, state: "failed", error: "It depends on something that couldn't be sent." });
            failed++;
            continue;
          }
          break;
        }
        let r: SendResult;
        try {
          r = await send(a, path, bodyInit(a.body));
        } catch {
          break; // no signal: try again later
        }
        if (r.status >= 200 && r.status < 300) {
          await db().put("results", a.id, r.json);
          await db().del("outbox", a.id);
          sent++;
        } else if (r.status === 409 && (r.json as { error?: string } | null)?.error === "in_progress") {
          break;
        } else if (r.status >= 400 && r.status < 500 && r.status !== 401 && r.status !== 408 && r.status !== 429) {
          await db().put("outbox", a.id, { ...a, state: "failed", error: r.message ?? `It was refused (${r.status}).` });
          failed++;
        } else {
          break; // server trouble or signed out: keep it for later
        }
      }
      const left = (await pending()).length;
      return { sent, failed, left };
    } finally {
      running = null;
      notify();
    }
  })());
}

/** Clear kept answers once nothing waiting needs them. */
export async function pruneResults(): Promise<void> {
  if ((await pending()).length) return;
  for (const r of await db().all<unknown>("results")) await db().del("results", r.key);
}
